import { useEffect, useRef, useState } from 'react';
import { polygonAreaKmSq, type GeoPolygon } from '@soc/shared';
import { simplifyRing } from '../../lib/geometry';

/**
 * Address → boundary search backed by OpenStreetMap Nominatim. Type a place,
 * pick a suggestion, and its administrative boundary (or, failing that, its
 * bounding box) is handed back as a GeoPolygon to seed the map draw.
 *
 * Nominatim usage policy: keep this debounced + low-volume. For production scale,
 * proxy through the backend (to set a descriptive User-Agent) or self-host.
 */

interface NominatimResult {
  place_id: number;
  display_name: string;
  /** [southLat, northLat, westLng, eastLng] as strings. */
  boundingbox: [string, string, string, string];
  geojson?:
    | { type: 'Polygon'; coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] }
    | { type: string; coordinates: unknown };
}

const NOMINATIM = 'https://nominatim.openstreetmap.org/search';

/**
 * Best-effort conversion of a Nominatim result into a single-ring GeoPolygon.
 * Boundaries come back with hundreds of vertices, so the ring is simplified
 * (Douglas–Peucker, capped vertex count) before it ever reaches the map / DB.
 */
function polygonFromResult(r: NominatimResult): GeoPolygon | null {
  const g = r.geojson;
  if (g && g.type === 'Polygon') {
    const ring = (g.coordinates as number[][][])[0];
    if (ring && ring.length >= 4) return { type: 'Polygon', coordinates: [simplifyRing(ring)] };
  }
  if (g && g.type === 'MultiPolygon') {
    const polys = g.coordinates as number[][][][];
    let best: number[][] | null = null;
    let bestArea = -1;
    for (const poly of polys) {
      const ring = poly[0];
      if (!ring) continue;
      const area = polygonAreaKmSq({ type: 'Polygon', coordinates: [ring] });
      if (area > bestArea) {
        bestArea = area;
        best = ring;
      }
    }
    if (best) return { type: 'Polygon', coordinates: [simplifyRing(best)] };
  }
  // Fallback: a rectangle from the bounding box.
  const [s, n, w, e] = r.boundingbox.map(Number);
  if ([s, n, w, e].every((v) => Number.isFinite(v))) {
    return {
      type: 'Polygon',
      coordinates: [
        [
          [w, s],
          [e, s],
          [e, n],
          [w, n],
          [w, s],
        ],
      ],
    };
  }
  return null;
}

export function AddressSearch({ onPick }: { onPick: (polygon: GeoPolygon) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const skipNext = useRef(false);

  useEffect(() => {
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    const term = q.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${NOMINATIM}?format=jsonv2&polygon_geojson=1&limit=5&q=${encodeURIComponent(term)}`;
        const res = await fetch(url, { signal: ctrl.signal, headers: { 'Accept-Language': 'en' } });
        if (res.ok) {
          setResults((await res.json()) as NominatimResult[]);
          setOpen(true);
        }
      } catch {
        /* aborted or network — ignore */
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const choose = (r: NominatimResult) => {
    const poly = polygonFromResult(r);
    skipNext.current = true;
    setQ(r.display_name);
    setOpen(false);
    setResults([]);
    if (poly) onPick(poly);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(16,22,31,0.92)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: `1px solid ${focused ? 'rgba(0,229,255,0.6)' : 'rgba(120,160,200,0.28)'}`,
          borderRadius: 11,
          padding: '0 13px',
          height: 42,
          boxShadow: focused
            ? '0 10px 28px rgba(0,0,0,0.5), 0 0 0 3px rgba(0,229,255,0.15)'
            : '0 6px 18px rgba(0,0,0,0.42)',
          transition: 'border-color .15s, box-shadow .15s',
        }}
      >
        <span style={{ color: focused ? 'var(--accent)' : 'var(--text-faint)', fontSize: 14, transition: 'color .15s' }}>⌕</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => {
            setFocused(true);
            if (results.length > 0) setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          placeholder="Search an address to import its boundary…"
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-primary)',
            fontSize: 13.5,
            outline: 'none',
          }}
        />
        {loading && <span className="mono" style={{ fontSize: 11, color: 'var(--text-faint)' }}>…</span>}
      </div>
      {open && results.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 5,
            margin: 0,
            padding: 4,
            listStyle: 'none',
            background: 'rgba(16,22,31,0.96)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(120,160,200,0.28)',
            borderRadius: 11,
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: '0 12px 30px rgba(0,0,0,0.5)',
          }}
        >
          {results.map((r) => (
            <li key={r.place_id}>
              <button
                type="button"
                onClick={() => choose(r)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: 12.5,
                  lineHeight: 1.4,
                  padding: '8px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-card)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {r.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
